import {
    createContext,
    type FC,
    type ReactNode,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { noop } from "@onshopfront/components";
import FontAwesomeLight from "../../Utilities/Platform/FontAwesome/FontAwesomeLight";

interface Props {
    label: string;
    icon: IconProp;
    children: Array<ReactNode>;
}

export const NavigationGroupChildFocus = createContext<() => void>(noop);

export const NavigationGroup: FC<Props> = ({ label, icon, children }) => {
    const childrenRef = useRef<HTMLUListElement>(null);
    const fromChildRef = useRef(false);

    const [ open, setOpen ] = useState(false);
    const [ height, setHeight ] = useState(0);

    const resizeObserver = useMemo(() => new ResizeObserver((entries) => {
        if(!entries.length) {
            return;
        }

        setHeight(entries[0].target.scrollHeight);
    }), []);

    let hasVisibleChild = false;

    for(let i = 0, l = children.length; i < l; i++) {
        if(children[i] !== false) {
            hasVisibleChild = true;
            break;
        }
    }

    useEffect(() => {
        const element = childrenRef.current;

        if(!element || !hasVisibleChild) {
            return;
        }

        resizeObserver.observe(element);

        return () => {
            resizeObserver.unobserve(element);
        };
    }, [ resizeObserver, hasVisibleChild ]);

    if(!hasVisibleChild) {
        return null;
    }

    return (
        <>
            <li
                className={
                    "flex cursor-pointer items-center py-1 my-1 bg-white dark:bg-dark-900 sticky ignore-default " +
                    "text-neutral-900 hover:text-neutral-500 dark:text-neutral-100 dark:hover:text-neutral-400 " +
                    "first:mt-0" + (
                        open ?
                            " top-0" :
                            ""
                    )
                }
                onClick={() => {
                    setOpen(prevState => !prevState);
                }}
            >
                <div className="flex-1 truncate text-xl">
                    <span className="inline-block mr-4 text-primary-dark dark:text-primary">
                        <FontAwesomeLight icon={icon} />
                    </span>
                    {label}
                </div>
                <FontAwesomeLight
                    icon="angle-right"
                    className={"transition-transform " + (
                        open ?
                            "rotate-90" :
                            "rotate-0"
                    )}
                />
            </li>
            <div
                className={
                    "transition-[height] overflow-hidden" + (
                        open ?
                            "" :
                            ""
                    )
                }
                style={{
                    height: open ? height : 0,
                }}
                onBlur={() => {
                    if(fromChildRef.current) {
                        setOpen(false);
                        fromChildRef.current = false;
                    }
                }}
            >
                <NavigationGroupChildFocus.Provider
                    value={() => {
                        fromChildRef.current = true;
                        setOpen(true);
                    }}
                >
                    <ul
                        ref={childrenRef}
                        className="border-box pt-1 pb-3 pl-6"
                    >
                        {children}
                    </ul>
                </NavigationGroupChildFocus.Provider>
            </div>
        </>
    );
};
